// Project timers — a segment of the open day-clock session that says which
// project, and which of its work items, the time went to. The server owns every
// rule (20261014190000_project_timer.sql): start_timer checks you in when
// you're out, resumes a break, refuses an earlier day's session and ends any
// running segment first, so Switch is just another start. stop_timer leaves you
// checked in; a break or a check-out ends the segment by itself. A Jira-linked
// project is timed on one of its live issues; any other project on one of its
// own work items (20261016170000_native_timer.sql, owner 2026-10-15). These
// helpers only call those doors and read what they wrote.
import { useSyncExternalStore } from 'react';
import { supabase } from './supabase';
import { captureError } from './posthog';
import { isModuleEnabled, can, type PermMap } from './roles';
import type { ClockResult } from './dayclock';

/** The running segment, with what the pill names it by. */
export type RunningTimer = {
  id: string;
  session_id: string;
  project_id: string;
  task_id: string | null;
  started_at: string;
  project: { name: string; code: string | null } | null;
  task: { code: string | null; title: string } | null;
};

/** A project this person may time: a member, not done. `jira` = it has a
 *  jira_links row, so the timer runs on one of its mirrored issues. */
export type TimeableProject = {
  id: string;
  name: string;
  code: string | null;
  client: string | null;
  jira: boolean;
};

/** What a timer runs on: a work item (on a linked project, a live Jira issue). */
export type TimerTask = {
  id: string;
  code: string | null;
  title: string;
  rank: string;
  jira_status: string | null;
  status: { name: string; category: 'todo' | 'progress' | 'done' } | null;
};

/** The note stop_timer keeps (project_time.note's check). */
export const NOTE_MAX = 2000;

// Shown only where it can work: the module is on in this build, and the role
// can edit projects (start_timer refuses without projects:edit). Membership is
// per project, so it's the picker's list, not this gate.
export const canTimeProjects = (perms: PermMap | undefined): boolean =>
  isModuleEnabled('projects') && can(perms, 'projects', 'edit');

// A project's shown ID is its key (20261016090000): a project made in Hyprflow
// has no code. Read as `code` so every display stays as it is.
const RUNNING =
  'id, session_id, project_id, task_id, started_at, project:projects(name, code:key), task:tasks(code, title)';

/** This person's running segment. `null` none, `undefined` the read failed. */
export async function loadRunningTimer(uid: string): Promise<RunningTimer | null | undefined> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('project_time')
    .select(RUNNING)
    .eq('user_id', uid)
    .is('ended_at', null)
    .limit(1);
  if (error) return undefined;
  return ((data as unknown as RunningTimer[] | null) ?? [])[0] ?? null;
}

/** The projects the picker offers. `null` is a read that failed. */
export async function loadTimeable(uid: string): Promise<TimeableProject[] | null> {
  if (!supabase) return [];
  const [mem, links] = await Promise.all([
    supabase
      .from('project_members')
      .select('project:projects(id, name, code:key, client, status)')
      .eq('user_id', uid),
    // RLS shows a member their own projects' links (J1, jira_links_read).
    supabase.from('jira_links').select('project_id'),
  ]);
  if (mem.error || links.error) return null;
  const linked = new Set(((links.data as { project_id: string }[]) ?? []).map((l) => l.project_id));
  type Row = { project: (Omit<TimeableProject, 'jira'> & { status: string }) | null };
  return ((mem.data as unknown as Row[]) ?? [])
    .map((r) => r.project)
    .filter((p): p is NonNullable<Row['project']> => !!p && p.status !== 'done')
    .map(({ status: _status, ...p }) => ({ ...p, jira: linked.has(p.id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const TASK_COLS = 'id, code, title, rank, jira_status, status:project_statuses(name, category)';
const done = (t: TimerTask) => (t.status?.category === 'done' ? 1 : 0);
// To do and in progress first, then the board's own order (rank, C-collated
// like the database), never the key's text, which put MRK-10 before MRK-9.
const timerOrder = (a: TimerTask, b: TimerTask) =>
  done(a) - done(b) || (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0);

/**
 * What a timer on this project can run on: a linked project's live issues, or
 * its own work items. Gone ones hidden. `null` the read failed.
 */
export async function loadTimerTasks(
  projectId: string,
  jira: boolean,
): Promise<TimerTask[] | null> {
  if (!supabase) return [];
  const q = supabase
    .from('tasks')
    .select(TASK_COLS)
    .eq('project_id', projectId)
    .is('jira_gone_at', null);
  const { data, error } = await (jira ? q.not('jira_issue_id', 'is', null) : q);
  if (error) return null;
  return ((data as unknown as TimerTask[]) ?? []).sort(timerOrder);
}

/** A work item made from the picker, so a project with none can be timed. */
export async function createTimerTask(
  projectId: string,
  title: string,
): Promise<{ ok: true; task: TimerTask } | { ok: false; message: string }> {
  if (!supabase) return { ok: false, message: 'Not connected.' };
  const { data, error } = await supabase
    .from('tasks')
    .insert({ project_id: projectId, title: title.trim().slice(0, 255) })
    .select(TASK_COLS)
    .single();
  if (error)
    return {
      ok: false,
      message:
        error.code === '42501'
          ? 'You can’t add work items to this project. Ask its lead.'
          : error.message,
    };
  return { ok: true, task: data as unknown as TimerTask };
}

type Rpc = { error: { message: string; code?: string } | null };

/**
 * Start (or switch to) a timer. 23505 is one_active_time_entry / the one-running
 * index, raced by another tab's check-in: re-read and retry once, as the
 * contract says. An earlier day's open session comes back as open_session, so
 * the pill re-reads and links to it the way a refused Check in does.
 */
export async function startTimer(projectId: string, taskId: string | null): Promise<ClockResult> {
  if (!supabase) return { ok: true };
  const sb = supabase;
  const call = (task: string | null): PromiseLike<Rpc> =>
    sb.rpc('start_timer', { p_project: projectId, p_task: task });
  let { error } = await call(taskId);
  if (error?.code === '23505') ({ error } = await call(taskId));
  if (!error) return { ok: true };
  if (error.code === '23505' || /still open/.test(error.message))
    return { ok: false, reason: 'open_session', message: error.message };
  // The server's own sentence ("you are not on that project", "that project is
  // closed…"): expected refusals, said to the person, not telemetry.
  if (error.code !== 'P0001') captureError(error, { op: 'projectTimer.start' });
  return { ok: false, reason: 'error', message: error.message };
}

/** Stop the running timer; you stay checked in. The note is optional. */
export async function stopTimer(note?: string): Promise<ClockResult> {
  if (!supabase) return { ok: true };
  const n = note?.trim().slice(0, NOTE_MAX);
  const { error } = await supabase.rpc('stop_timer', { p_note: n || null });
  if (!error) return { ok: true };
  if (error.message.includes('no timer is running'))
    return { ok: false, reason: 'refused', message: 'That timer had already stopped.' };
  captureError(error, { op: 'projectTimer.stop' });
  return { ok: false, reason: 'error', message: error.message };
}

// ── The running timer, shared ────────────────────────────────────────────────
// The pill reads it (and stays live on project_time's realtime); anything else
// that wants to know — a project card's LiveTimerBadge — subscribes here
// instead of opening its own channel per card.
let current: RunningTimer | null = null;
const listeners = new Set<() => void>();
export const setRunningTimer = (t: RunningTimer | null): void => {
  if (t?.id === current?.id && t?.task_id === current?.task_id) return;
  current = t;
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};
export const useRunningTimer = (): RunningTimer | null =>
  useSyncExternalStore(subscribe, () => current);
